import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

interface SearchSelectProps<T> {
  label?: string;
  value: T | null;
  onChange: (item: T | null) => void;
  // Clave de caché de las búsquedas (sin el texto buscado, que se añade solo).
  queryKey: readonly unknown[];
  // Busca EN EL SERVIDOR y devuelve un número limitado de resultados: un desplegable normal con
  // todos los jugadores/usuarios/parejas deja de ser usable en cuanto hay cientos.
  fetchOptions: (search: string) => Promise<T[]>;
  getKey: (item: T) => string | number;
  getLabel: (item: T) => string;
  getHint?: (item: T) => string | null | undefined;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  // Si el servidor devuelve tantos resultados o más, se avisa de que hay más y hay que afinar.
  resultCap?: number;
}

// Selector con búsqueda para entidades numerosas. El panel de opciones FLOTA sobre la página (no la
// empuja: abrirlo nunca mueve el contenido), se coloca solo arriba/abajo según haya sitio y tiene el
// ancho del campo. Usa Popover de Radix, que convive bien con los modales (Dialog) del panel.
export function SearchSelect<T>({
  label,
  value,
  onChange,
  queryKey,
  fetchOptions,
  getKey,
  getLabel,
  getHint,
  placeholder = "Selecciona…",
  searchPlaceholder = "Buscar…",
  disabled,
  clearable,
  resultCap = 25,
}: SearchSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search.trim(), 250);

  const { data, isFetching } = useQuery({
    queryKey: [...queryKey, debounced],
    queryFn: () => fetchOptions(debounced),
    enabled: open,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  function pick(item: T | null) {
    onChange(item);
    setOpen(false);
    setSearch("");
  }

  const selectedKey = value ? getKey(value) : null;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {label && <span className="text-sm font-medium text-ink-600">{label}</span>}
      <div className="flex items-stretch gap-1.5">
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={`flex min-w-0 flex-1 items-center justify-between gap-2 rounded-xl border bg-white px-3 py-2 text-left text-sm outline-none transition disabled:cursor-not-allowed disabled:bg-ink-50 ${
                open ? "border-brand-500 ring-2 ring-brand-500/20" : "border-ink-100"
              }`}
            >
              <span className={`truncate ${value ? "text-ink-800" : "text-ink-400"}`} title={value ? getLabel(value) : undefined}>
                {value ? getLabel(value) : placeholder}
              </span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-ink-300 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align="start"
              sideOffset={6}
              collisionPadding={12}
              style={{ width: "var(--radix-popover-trigger-width)" }}
              className="z-[70] rounded-xl border border-ink-100 bg-white p-2 shadow-lg focus:outline-none"
            >
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-ink-300" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-lg border border-ink-100 py-2 pr-3 pl-8 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              <ul className="mt-2 max-h-56 overflow-y-auto">
                {!data ? (
                  <li className="px-2 py-2 text-sm text-ink-400">Buscando…</li>
                ) : !data.length ? (
                  <li className="px-2 py-2 text-sm text-ink-400">{isFetching ? "Buscando…" : "Sin resultados"}</li>
                ) : (
                  data.map((item) => {
                    const key = getKey(item);
                    const selected = key === selectedKey;
                    const hint = getHint?.(item);
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => pick(item)}
                          className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-ink-50 ${
                            selected ? "bg-brand-50 text-brand-700" : "text-ink-700"
                          }`}
                        >
                          <span className="min-w-0">
                            <span className="block truncate">{getLabel(item)}</span>
                            {hint && <span className="block truncate text-xs text-ink-400">{hint}</span>}
                          </span>
                          {selected && <Check className="h-4 w-4 shrink-0" />}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
              {data && data.length >= resultCap && (
                <p className="mt-1 px-2 text-xs text-ink-400">Hay más resultados: escribe para afinar la búsqueda.</p>
              )}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
        {clearable && value && !disabled && (
          <button
            type="button"
            aria-label="Quitar selección"
            title="Quitar selección"
            onClick={() => pick(null)}
            className="shrink-0 rounded-xl border border-ink-100 bg-white px-2.5 text-ink-400 transition-colors hover:bg-ink-50 hover:text-ink-700"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
